import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { createSlice } from '@reduxjs/toolkit';
import { REVIEW_LISTING_TYPE, GUIDE_LISTING_TYPE, NEWS_LISTING_TYPE } from '../../util/types';
import { parse } from '../../util/urlHelpers';

const RESULT_PAGE_SIZE = 8;
// ================ Initial State ================ //

const initialState = {
  currentPageResultIds: [],
  pagination: null,
  searchInProgress: false,
  searchArticlesError: null,
};

// ================ Reducer ================ //

const articlesPageSlice = createSlice({
  name: 'ArticlesPage',
  initialState,
  reducers: {
    searchArticlesRequest(state) {
      state.searchInProgress = true;
      state.searchArticlesError = null;
    },
    searchArticlesSuccess(state, action) {
      state.currentPageResultIds = action.payload.listingIds;
      state.pagination = action.payload.pagination;
      state.searchInProgress = false;
    },
    searchArticlesError(state, action) {
      state.searchInProgress = false;
      state.searchArticlesError = action.payload;
    },
  },
});

export default articlesPageSlice.reducer;

// ================ Action creators ================ //

const {
  searchArticlesRequest,
  searchArticlesSuccess,
  searchArticlesError,
} = articlesPageSlice.actions;

const REVIEW_PAGE_SIZE = 6;
const GUIDE_PAGE_SIZE = 4;
const NEWS_PAGE_SIZE = 4;

const makeListingQuery = (sdk, listingType, perPage, page = 1, extraParams = {}) => {
  return sdk.listings.query({
    pub_listingType: listingType,
    ...extraParams,
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': ['title', 'geolocation', 'price', 'publicData', 'metadata', 'createdAt'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',
      'variants.default',
    ],
    'limit.images': 1,
    page,
    perPage,
    sort: '-createdAt',
  });
};

// ================ Thunks ================ //
export const searchArticles = (queryParams = {}) => (dispatch, getState, sdk) => {
  dispatch(searchArticlesRequest());
  const { page = 1, keywords } = queryParams;
  const keywordsMaybe = keywords ? { keywords } : {};

  return Promise.all([
    makeListingQuery(sdk, REVIEW_LISTING_TYPE, REVIEW_PAGE_SIZE, page, keywordsMaybe),
    makeListingQuery(sdk, GUIDE_LISTING_TYPE, GUIDE_PAGE_SIZE, page, keywordsMaybe),
    makeListingQuery(sdk, NEWS_LISTING_TYPE, NEWS_PAGE_SIZE, page, keywordsMaybe),
  ])
    .then(([reviewsResponse, guidesResponse, newsResponse]) => {
      // Add all entities to the marketplace data store
      dispatch(addMarketplaceEntities(reviewsResponse));
      dispatch(addMarketplaceEntities(guidesResponse));
      dispatch(addMarketplaceEntities(newsResponse));

      // Combine IDs from all three responses
      const listingIds = [
        ...reviewsResponse.data.data.map(l => l.id),
        ...guidesResponse.data.data.map(l => l.id),
        ...newsResponse.data.data.map(l => l.id),
      ];

      // Use reviews pagination as the primary (or create a synthetic one)
      const pagination = reviewsResponse.data.meta;

      dispatch(searchArticlesSuccess({ listingIds, pagination }));
    })
    .catch(e => {
      dispatch(searchArticlesError(storableError(e)));
      throw e;
    });
};

// ================ Page load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  return dispatch(searchArticles(queryParams));
};
